import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {VehicleRepairRepo} from "../api/vehicle/vehicle-repair.repo";
import {DuckDbHelper, sqlDirection, sqlNumber, sqlSelectedIn, sqlText, sqlVehicleFilter} from "./duckdb.helper";
import {OrgDto} from "../dto/org.dto";
import {WrapperDto} from "../dto/wrapper.dto";
import {VehicleRepairDto} from "../dto/vehicle/vehicle-repair.dto";
import {SearchVehicleRepairRes} from "../dto/vehicle/search-vehicle-repair.res";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {LoggingService} from "../../services/logger/logging.service";
import {isDefined} from "../../libs/utils/epic.utils";

@Injectable({providedIn: 'root'})
export class VehicleRepairDuckDb extends VehicleRepairRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override getById(_org: OrgDto, id: string, vehicleId: string): Observable<WrapperDto<VehicleRepairDto>> {
    const select = `
      SELECT r.id         AS id,
             r.recorded   AS recorded,
             r.notes      AS notes,
             r.completed  AS completed,
             r.on_invoice AS onInvoice,
             r.vehicle_id AS vehicleId
      FROM vehicle_repair r
      WHERE r.id = ${sqlText(id)}
        AND r.vehicle_id = ${sqlText(vehicleId)}`;
    return this.duck.sql(select).pipe(map((res: any) =>
      DuckDbHelper.data('VehicleRepairDto', new VehicleRepairDto(DuckDbHelper.firstRes(res)))));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchVehicleRepairRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT r.id         AS id,
             r.recorded   AS recorded,
             r.notes      AS notes,
             r.completed  AS completed,
             r.on_invoice AS onInvoice,
             r.vehicle_id AS vehicleId,
             COUNT(*)        OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING) AS total_results
      FROM vehicle_repair r
      WHERE ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const rows: any[] = DuckDbHelper.hasRes(res) ? res : [];
      const result = new SearchVehicleRepairRes({
        items: rows,
        hits: rows.length > 0 ? rows[0].total_results : 0,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchVehicleRepairRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      case 'recorded':
        field = 'r.recorded';
        break;
      case 'notes':
        field = 'r.notes';
        break;
      case 'onInvoice':
        field = 'r.on_invoice';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 'r.recorded';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildFilter(request: SearchRequest): string {
    return [sqlVehicleFilter(request, 'r.vehicle_id'), sqlSelectedIn(request, 'onInvoice', 'r.on_invoice')]
      .filter((f) => isDefined(f))
      .join(' AND ');
  }
}
